import React, { useMemo, useState } from "react";
import { T, STATUS, SERIF, GOLD_INK } from "../theme/tokens.js";
import { Panel, Empty, Btn, Kpi, Chip, Label } from "../components/ui/index.jsx";
import RegisterTable from "../components/RegisterTable.jsx";
import ScheduleTree from "../components/ScheduleTree.jsx";
import DrillPanel from "../components/DrillPanel.jsx";
import ScheduleTable from "../components/ScheduleTable.jsx";
import KeyDates from "../components/KeyDates.jsx";
import { useSchedule } from "../lib/schedule/useSchedule.js";
import { useRegisters } from "../lib/useRegister.js";
import { scheduleModel, upcomingWindow, isComplete } from "../lib/calc.js";
import { fmtDate, today } from "../lib/format.js";

const ROW_H = 28;
const NAME_W = 200;
const PCT_W = 46;

/* The bar was navy with a slightly-less-navy fill for the share complete, and
   at 14 pixels high on a cream page nobody could tell the two apart. So the
   bar is now a pale track with a navy edge, and the part that is done is the
   same green that means done everywhere else in this app.

   The percentage is printed beside the name rather than at the end of the bar,
   because a length is a comparison and a number is a fact, and because a two
   year program is wider than the panel: anything past the last month scrolls
   out of sight, and the number is the part nobody should have to scroll for. */
const TRACK = "#C6CEDC";

/* Navy bars for phases, gold diamonds for milestones, a red line for today.
   The one drawing rule: nothing here computes a position. Every left and
   width is a fraction that came out of scheduleModel, so what is on screen
   and what the monthly deck will say are the same arithmetic. */
function Gantt({ model, onOpen }) {
  const { months, bars, milestones, todayFraction } = model;
  const pct = (n) => `${(n * 100).toFixed(3)}%`;
  const ref = today();

  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ minWidth: Math.max(560, months.length * 62) }}>
        {/* Month header */}
        <div style={{
          display: "flex", marginLeft: NAME_W + PCT_W,
          borderBottom: `2px solid ${T.navy}`, position: "relative",
        }}>
          {months.map((m) => (
            <div key={m.key} style={{
              flex: 1, textAlign: "center", fontSize: 10, fontWeight: 700,
              color: T.navy, letterSpacing: "0.06em", padding: "2px 0",
              borderLeft: `1px solid ${T.line}`,
            }}>{m.label}</div>
          ))}
        </div>

        {/* Rows */}
        <div style={{ position: "relative" }}>
          {/* Month gridlines and the today line sit behind every bar. */}
          <div style={{
            position: "absolute", left: NAME_W + PCT_W, right: 0, top: 0, bottom: 0,
            display: "flex", pointerEvents: "none",
          }}>
            {months.map((m) => (
              <div key={m.key} style={{ flex: 1, borderLeft: `1px solid ${T.line}` }} />
            ))}
          </div>
          {todayFraction !== null && (
            <div title={`Today, ${fmtDate(today())}`} style={{
              position: "absolute", top: 0, bottom: 0,
              left: `calc(${NAME_W + PCT_W}px + (100% - ${NAME_W + PCT_W}px) * ${todayFraction})`,
              width: 2, background: T.risk, opacity: 0.8, pointerEvents: "none",
            }} />
          )}

          {bars.map((b) => {
            /* Late is the finish date behind us with work still on it. It is
               the one thing a phase bar should say without being clicked. */
            const late = b.finish && b.finish < ref && (b.percent ?? 0) < 100;
            const openable = (b.rows?.length ?? 0) > 0;
            return (
              <div key={b.id} style={{ display: "flex", alignItems: "center", height: ROW_H }}>
                <div style={{
                  width: NAME_W, paddingRight: 10, fontSize: 12, color: T.ink,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  paddingLeft: (b.level - 1) * 12,
                }} title={b.title}>{b.title}</div>
                <div style={{
                  width: PCT_W, paddingRight: 10, textAlign: "right", fontSize: 11.5,
                  whiteSpace: "nowrap",
                  color: late ? STATUS.overdue.ink : T.soft,
                  fontWeight: late ? 700 : 400,
                }}>
                  {b.percent === null ? "" : `${b.percent}%`}
                </div>
                <div style={{ flex: 1, position: "relative", height: ROW_H }}>
                  <div
                    onClick={openable ? () => onOpen?.(b) : undefined}
                    title={`${b.title}: ${fmtDate(b.start)} to ${fmtDate(b.finish)}${
                      b.percent === null ? "" : `, ${b.percent}% complete`}${
                      openable ? `. Click for the ${b.rows.length} rows behind it.` : ""}`}
                    style={{
                      position: "absolute", top: 5, height: 17,
                      left: pct(b.left),
                      width: `max(8px, ${pct(Math.max(0, b.right - b.left))})`,
                      background: TRACK,
                      border: `1.5px solid ${late ? STATUS.overdue.fill : T.navy}`,
                      borderRadius: 3, overflow: "hidden",
                      cursor: openable ? "pointer" : "default",
                    }}
                  >
                    {b.percent > 0 && (
                      <div style={{
                        height: "100%", width: `${Math.min(100, b.percent)}%`,
                        background: STATUS.complete.fill,
                      }} />
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Milestones share one row, so the diamonds read as a timeline
              rather than as a staircase. */}
          {milestones.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", height: ROW_H + 6 }}>
              <div style={{ width: NAME_W + PCT_W, paddingRight: 10, fontSize: 12,
                            color: T.navy, fontWeight: 700 }}>Milestones</div>
              <div style={{ flex: 1, position: "relative", height: ROW_H + 6 }}>
                {milestones.map((m, i) => (
                  <div key={i}
                    onClick={m.row ? () => onOpen?.({
                      kind: "milestones", title: m.title, rows: [m.row],
                    }) : undefined}
                    title={`${m.title}, ${fmtDate(m.date)}${m.status ? ` (${m.status})` : ""}`}
                    style={{
                      position: "absolute", top: 9, left: pct(m.at), width: 13, height: 13,
                      marginLeft: -6.5, transform: "rotate(45deg)",
                      background: m.status === "Complete" ? T.gold : T.panel,
                      border: `2px solid ${T.gold}`,
                      cursor: m.row ? "pointer" : "default",
                    }} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Schedule({ contract }) {
  /* The engine only reads what it is given, so the page fetches once and both
     halves share it: the timeline above, the editable plan below. */
  const schedule = useSchedule(contract?.id);
  const { data } = useRegisters(
    ["schedule_stages", "milestones", "deliverables"], contract?.id,
  );
  return <ScheduleView contract={contract} data={data} schedule={schedule} />;
}

/* showRegisters is off in the visual preview, for the same reason as on the
   finance page: the editable tables fetch, and everything above them does not. */
export function ScheduleView({ contract, data, schedule, showRegisters = true }) {
  const [asTable, setAsTable] = useState(false);
  const [drill, setDrill] = useState(null);

  /* Every number on this page carries the rows it was counted from, so a
     click opens them here rather than sending anybody hunting through the
     schedule of record for the eleven activities inside one phase. */
  const open = (kind, title, rows) => setDrill(rows?.length ? { kind, title, rows } : null);
  const openBar = (b) => open(b.kind ?? "schedule_stages",
    b.rows?.length === 1 ? b.title : `${b.title}: ${b.rows.length} rows`, b.rows);
  const model = useMemo(() => (data ? scheduleModel({
    stages: data.schedule_stages, milestones: data.milestones,
    deliverables: data.deliverables,
  }) : null), [data]);

  const upcoming = useMemo(() => (data ? upcomingWindow({
    milestones: data.milestones, deliverables: data.deliverables,
  }, 30) : []), [data]);

  if (!data || !model) return <Empty text="Loading..." />;

  const openMilestones = data.milestones.filter((m) => m.status !== "Complete");
  const lateMilestones = openMilestones.filter((m) => m.date && m.date < today());
  const phaseRows = model.bars.flatMap((b) => b.rows ?? []);
  const phaseKind = model.bars[0]?.kind ?? "schedule_stages";
  const clickable = { cursor: "pointer" };

  return (
    /* minmax(0, 1fr) rather than 1fr: a grid item defaults to a minimum
       width of its content, which lets a wide table or a long Gantt push
       the panel out past the page instead of scrolling inside it. */
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "minmax(0, 1fr)" }}>
      <div style={{ display: "grid", gap: 12,
                    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}>
        <div style={clickable} onClick={() => open(phaseKind, "Everything on the timeline", phaseRows)}>
          <Kpi label="Phases" value={model.bars.length}
               sub={model.derived
                 ? `Derived from ${model.basis}`
                 : `From the schedule of record, ${model.activityCount} activities`} />
        </div>
        <div style={clickable} onClick={() => open("milestones", "All milestones", data.milestones)}>
          <Kpi label="Milestones" value={data.milestones.length}
               sub={`${openMilestones.length} still open`} color={GOLD_INK} />
        </div>
        <div style={clickable} onClick={() => open("milestones", "Milestones past due", lateMilestones)}>
          <Kpi label="Milestones past due" value={lateMilestones.length}
               sub={lateMilestones.length ? "Open with a date behind us" : "None behind"}
               color={lateMilestones.length ? T.risk : T.navy} />
        </div>
        <div style={clickable} onClick={() => open("deliverables", "Deliverables due in the next 30 days",
          upcoming.filter((i) => i.kind === "deliverable").map((i) => i.row))}>
          <Kpi label="Next 30 days" value={upcoming.length}
               sub="Milestones and deliverables due" color={T.steel} />
        </div>
      </div>

      {/* Where the program stands, before the detail of how it got there. */}
      {schedule && showRegisters && <KeyDates schedule={schedule} contract={contract} />}

      {drill && <DrillPanel drill={drill} onClose={() => setDrill(null)} />}

      {/* The plan itself, live. Above this is the read of it; this is the
          thing a person actually works in while a vendor is talking. */}
      {schedule && showRegisters && (
        <ScheduleTable contract={contract} schedule={schedule} />
      )}

      <Panel
        title="Schedule overview"
        right={model.start && (
          <Chip color={T.steel}>{fmtDate(model.start)} to {fmtDate(model.finish)}</Chip>
        )}
      >
        {model.bars.length === 0 && model.milestones.length === 0 ? (
          <Empty text="Nothing here carries a date yet. Load a schedule, or add milestones and dated deliverables, and the timeline draws itself." />
        ) : (
          <>
            <Gantt model={model} onOpen={openBar} />
            <div style={{ fontSize: 12, color: T.soft, marginTop: 10, lineHeight: 1.6 }}>
              {model.derived
                ? `This contract has no schedule rows yet, so the phases are built from the ${model.basis}: each bar runs from the earliest to the latest date in that group, and the lighter fill is the share complete. Load the real schedule and these are replaced by what the program actually published.`
                : `Bars are the phases as the schedule records them, with the lighter fill as percent complete. ${
                    model.hidden
                      ? `The ${model.hidden} activities underneath them are in the schedule of record below rather than on the chart, because a bar per task would be a wall rather than a schedule.`
                      : ""}`}
              {" "}Gold diamonds are milestones, filled once complete. The red line is today.
              A bar outlined in red finished behind us with work still on it.
              Click any bar or diamond to open what is inside it.
            </div>
          </>
        )}
      </Panel>

      <Panel title="Next 30 days">
        {upcoming.length === 0 ? (
          <Empty text="Nothing falls due in the next 30 days." />
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <tbody>
              {upcoming.slice(0, 20).map((i, n) => (
                <tr key={n}
                  onClick={i.row ? () => open(
                    i.kind === "milestone" ? "milestones" : "deliverables", i.title, [i.row]) : undefined}
                  style={{ borderBottom: `1px solid ${T.line}`,
                           cursor: i.row ? "pointer" : "default" }}>
                  <td style={{ padding: "6px 9px", whiteSpace: "nowrap", width: 1 }}>
                    {fmtDate(i.date)}
                  </td>
                  <td style={{ padding: "6px 9px", whiteSpace: "nowrap", width: 1 }}>
                    <Chip color={i.kind === "milestone" ? GOLD_INK : T.steel}>
                      {i.kind === "milestone" ? "Milestone" : "Deliverable"}
                    </Chip>
                  </td>
                  <td style={{ padding: "6px 9px", overflowWrap: "anywhere" }}>{i.title}</td>
                  <td style={{ padding: "6px 9px", color: T.soft, whiteSpace: "nowrap" }}>
                    {i.vendor || ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {upcoming.length > 20 && (
          <div style={{ fontSize: 12, color: T.soft, marginTop: 8 }}>
            Showing the first 20 of {upcoming.length}.
          </div>
        )}
      </Panel>

      {showRegisters && (
        <>
      <Panel title="Milestones">
        <RegisterTable registerKey="milestones" contract={contract} />
      </Panel>

      <Panel title="Schedule of record"
        right={<Btn small kind="ghost" onClick={() => setAsTable(!asTable)}>
          {asTable ? "Show as plan" : "Show as table"}
        </Btn>}>
        {asTable ? (
          <RegisterTable registerKey="schedule_stages" contract={contract}
            note="The flat table, for adding an activity or correcting one. The plan view above is the same rows arranged as the plan." />
        ) : (
          <ScheduleTree rows={data.schedule_stages} />
        )}
      </Panel>
        </>
      )}
    </div>
  );
}
